<template lang="pug">
div.neutral200
  v-container.pa-8(style="max-width:1100px")
    div.d-flex.justify-center.mt-16(v-if="isFetching")
      v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

    template(v-else)
      v-row(no-gutters align="center")
        v-col.shrink
          v-btn.ml-n3(text icon @click="goToPackages" height)
            v-icon(color="basic800" :size="24") mdi-arrow-left
        v-col.ml-3
          .text-2xl.font-semibold.neutral700--text {{ isHqPackage ? 'View package' : (id ? 'Edit package' : 'Add package') }}

      v-form(ref="form")
        //- ==================== HQ VIEW MODE ====================
        template(v-if="isHqPackage")
          //- Card 1: Package details
          v-card.pa-6.mt-4.round-xl(outlined)
            .text-md.font-semibold.neutral700--text Package details
            v-row.mt-3
              v-col(cols="4")
                div.text-sm.font-semibold.neutral500--text Package name
                div.text-sm.neutral700--text.mt-2 {{ packageName }}
                yz-badge.mt-1(size="sm" color="blue-100" text-color="blue-500" border) HQ

          //- Card 2: Default pricing + Override
          v-card.pa-6.mt-4.round-xl(outlined)
            .text-md.font-semibold.neutral700--text Default pricing
            .text-sm.neutral700--text.mt-1 Set the default price for this package. This pricing will be shared across all branches.
            v-row.mt-4
              v-col(cols="4")
                div.text-sm.font-semibold.neutral500--text Price
                div.text-sm.neutral700--text.mt-2 {{ hqPrice | displayPrice($currency) }}
              v-col(cols="4")
                div.text-sm.font-semibold.neutral500--text Bundle value
                div.text-sm.neutral700--text.mt-2 {{ packageValue | displayPrice($currency) }}
              v-col(cols="4")
                div.text-sm.font-semibold.neutral500--text Discount
                div.text-sm.neutral700--text.mt-2 {{ hqDiscount ? hqDiscount + '%' : '—' }}
            v-divider.mt-6
            template(v-if="canEditPriceOverride")
              div.d-flex.align-center.justify-space-between.mt-6
                div
                  .text-md.font-semibold.neutral700--text Default pricing override
                  .text-sm.neutral700--text.mt-1 This pricing will override the default pricing and adjusted according to your selected branches and pricing setup.
                yz-btn(v-if="syncHqPrice && !isAddingOverride" label="Add override price" size="md" @click="onAddOverride")
              //- State B: Has existing override or adding new one
              template(v-if="!syncHqPrice || isAddingOverride")
                v-row.mt-4
                  v-col(cols="4")
                    div.text-sm.font-semibold.neutral500--text Price
                    v-text-field.text-sm.mt-2.round-xl(v-model="packagePrice" placeholder="0" outlined dense height="48" :prefix="$currency.symbol" :rules="rules.not_empty" type="number" hide-details="auto")
                  v-col(cols="4")
                    div.text-sm.font-semibold.neutral500--text Bundle value
                    v-text-field.text-sm.mt-2.round-xl(:value="packageValue" placeholder="0" outlined dense height="48" :prefix="$currency.symbol" type="number" readonly background-color="neutral300" hide-details="auto")
                  v-col(cols="4")
                    div.text-sm.font-semibold.neutral500--text Discount
                    v-text-field.text-sm.mt-2.round-xl(:value="packageDiscount" placeholder="0" outlined dense height="48" suffix="%" type="number" readonly background-color="neutral300" hide-details="auto")

          //- Card 3: Treatments (read-only)
          v-card.pa-6.mt-4.round-xl(outlined)
            .text-md.font-semibold.neutral700--text Attached treatments
            v-data-table.mt-2(:headers="hqReadonlyHeaders" :items="treatments" item-key="id" :items-per-page="pageSize" :hide-default-header="treatments.length === 0" hide-default-footer disable-sort :header-props="{ sortIcon: '$yzi-triangle' }")
              template(#no-data)
                .text-center.py-6
                  .text-sm.neutral500--text No treatments attached
              template(v-slot:item.name="{ item }")
                div.py-2
                  .text-sm.font-semibold.neutral700--text {{ item.name }}
              template(v-slot:item.type="{ item }")
                div.py-2
                  .text-sm.neutral700--text(v-if="item.service") Service
                  .text-sm.neutral700--text(v-else) Inventory
              template(v-slot:item.price="{ item }")
                div.py-2
                  .text-sm.neutral700--text {{ item.price | displayPrice($currency) }}
              template(v-slot:item.stock="{ item }")
                div.py-2
                  .text-sm.neutral700--text(v-if="item.inventory_enabled") {{ item.inventory_qty }} {{ getStockUnit(item) }}
                  .text-sm.neutral700--text(v-else) -
              template(v-slot:item.quantity="{ item }")
                div.py-2
                  .text-sm.neutral700--text {{ item.quantity }} {{ item.selected_uom || item.uom || '' }}

        //- ==================== EDIT/CREATE MODE ====================
        template(v-else)
          v-card.pa-6.mt-4.round-xl(outlined)
            div.d-flex.align-center
              .text-md.font-semibold.neutral700--text Package details
              v-spacer
              yz-btn(v-if="id" label="View package history" size="sm" @click="$router.push({ name: 'PackageHistory', params: { id, origin: 'EditClinicPackage' } })")
            v-row.mt-3
              v-col(cols="4")
                div.text-sm.font-semibold.neutral500--text Package name
                v-text-field.text-sm.mt-2.round-xl(v-model="packageName" :placeholder="$t('name')" outlined dense height="48" :rules="rules.not_empty" hide-details="auto")
            v-row.mt-3
              v-col(cols="4")
                div.text-sm.font-semibold.neutral500--text Price
                v-text-field.text-sm.mt-2.round-xl(v-model="packagePrice" placeholder="0" outlined dense height="48" :prefix="$currency.symbol" :rules="rules.not_empty" type="number" :background-color="allowedPriceEdit ? '' : 'neutral300'" :disabled="!allowedPriceEdit" hide-details="auto")
              v-col(cols="4")
                div.text-sm.font-semibold.neutral500--text Bundle value
                v-text-field.text-sm.mt-2.round-xl(v-model="packageValue" placeholder="0" outlined dense height="48" :prefix="$currency.symbol" type="number" readonly background-color="neutral300" hide-details="auto")
              v-col(cols="4")
                div.text-sm.font-semibold.neutral500--text Discount
                v-text-field.text-sm.mt-2.round-xl(v-model="packageDiscount" placeholder="0" outlined dense height="48" suffix="%" type="number" readonly background-color="neutral300" hide-details="auto")

          //- Attach inventory an services
          v-card.pa-6.mt-4.round-xl(outlined)
            .text-md.font-semibold.neutral700--text Attach inventory and services
            .text-sm.neutral700--text.mt-2 Search and select your inventory and services. You can set your quantity and price
            v-autocomplete.text-sm.round-xl.mt-6.autocmplt(ref="autocmplt" v-model="product" :items="products" item-text="name" return-object :search-input.sync="searchProducts" placeholder="Search and select here" :loading="isSearching" outlined dense clearable height="48" hide-details="auto" hide-no-data attach  append-icon=""  @change="addToList(product)" @blur="clearAutocomplete")
              template(#prepend-inner)
                v-icon.mt-1(size="22") $yzi-search-sm
              template(#item="{ index, item }")
                div.py-3.px-2
                  .text-sm.font-normal(v-if="item.uom") {{ item.name }}  ({{item.uom}})
                  .text-sm.font-normal(v-else) {{ item.name }}
                  .d-flex
                    .d-flex.align-center.mr-4
                      template(v-if="hasStock(item)")
                        v-icon.mr-1(size="11" color="success") $yzi-circle
                        .text-xs.font-normal.neutral500--text {{ $t('in_stock') }}
                      template(v-else)
                        v-icon.mr-1(size="11" color="danger") $yzi-circle
                        .text-xs.font-normal.neutral500--text {{ $t('out_of_stock') }}
                    .d-flex.align-center.mr-4
                      v-icon.mr-1(size="13") {{ getTypeIcon(item) }}
                      .text-xs.font-normal.neutral500--text {{ item.type | capitalize }}
                    template(v-if="item.groups && item.groups.length > 0")
                      .d-flex.align-center.mr-4
                        v-icon.mr-1(size="13") $yzi-folder
                        .text-xs.font-normal.neutral500--text {{ item.groups.join(', ') | capitalize }}
                  .text-xxs.font-normal.italic.danger500--text(v-if="item.hasError") {{ item.errorMessage }}

            v-data-table.mt-2(:headers="headers" :items="treatments" item-key="id" :items-per-page="pageSize" :hide-default-header="treatments.length === 0" hide-default-footer disable-sort :loading="isSelecting" :header-props="{ sortIcon: '$yzi-triangle' }")
              template(#no-data)
                div.empty-box.mt-6.pa-8(align="center")
                  v-avatar(color="neutral300" size="54")
                    v-icon(color="neutral500" size="30") $yzi-colors
                  div.text-center.text-md.font-semibold.neutral700--text.mt-6 No inventory or services selected
                  div.text-center.text-sm.font-normal.neutral500--text.mt-1 Selected inventory or service will be displayed here
              //- name
              template(v-slot:item.name="{ item }")
                div.py-2
                  .text-sm.font-semibold.neutral700--text {{ item.name }}
              //- type
              template(v-slot:item.type="{ item }")
                div.py-2
                  .text-sm.neutral700--text(v-if="item.service") Service
                  .text-sm.neutral700--text(v-else) Inventory
              //- price
              template(v-slot:item.price="{ item }")
                div.py-2
                  .text-sm.neutral700--text {{ item.price | displayPrice($currency) }}
              //- stock
              template(v-slot:item.stock="{ item }")
                div.py-2
                  .text-sm.neutral700--text(v-if="item.inventory_enabled") {{ item.inventory_qty }} {{ getStockUnit(item) }}
                  .text-sm.neutral700--text(v-else) -
              //- quantity
              template(v-slot:item.quantity="{ item }")
                div.d-flex.align-center.gap-2(style="min-width: 100px;")
                  v-text-field.text-sm.my-2.round-xl(v-model="item.quantity" placeholder="0" outlined dense height="48" :rules="rules.has_quantity" type="number" hide-details style="max-width: 80px;")
                  v-select.text-sm.my-2.round-xl(v-if="item.uom_conversion_enabled && item.uom_conversions && item.uom_conversions.length" v-model="item.selected_uom" :items="getUomOptions(item)" :rules="getUomRules(item)" outlined dense height="48" hide-details="auto" style="max-width: 120px;")
                  .text-sm.neutral500--text(v-else-if="item.uom") {{ item.uom }}
              //- action
              template(v-slot:item.action="{ item }")
                div.d-flex.justify-end
                  v-btn(icon small @click="deleteTreatment(item)")
                    v-icon $yzi-trash-03

        //- v-card.pa-6.mt-4.round-xl(outlined v-if="Auth('page', '/admin/clinic/appointment')")
          div.d-flex
            div
              .text-md.font-semibold.neutral700--text Make this service available online
              .text-sm.neutral700--text List this service in your appointment page
            v-spacer
            div
              y-switch(v-model="packageForm.booking_enabled")
          template(v-if="packageForm.booking_enabled")
            v-divider.mt-5
            service-details(extend)
            v-divider.basic500.my-5
            service-request
            v-divider.basic500.my-5
            service-settings
            v-divider.basic500.my-5
            service-pre-questions
            v-divider.basic500.my-5
            service-pre-instructions
            v-divider.basic500.my-5
            service-post-instructions
            v-divider.basic500.my-5
            service-provider

    v-footer.white(v-if="!isHqPackage || canEditPriceOverride" app inset padless elevation="3")
      v-container.px-8.py-2
        div.d-flex.align-center
          v-spacer
          y-button(:label="isHqPackage ? $t('save') : (id ? $t('save') : $t('finish'))" color="neutral700" dark :loading="isLoading" @click="saveData")

</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
// import ServiceDetails from '@/components/clinic/manage/ServiceDetails.vue'
// import ServiceSettings from '@/components/clinic/manage/ServiceSettings.vue'
// import ServiceRequest from '@/components/clinic/manage/ServiceRequest.vue'
// import ServicePreQuestions from '@/components/clinic/manage/ServicePreQuestions.vue'
// import ServicePreInstructions from '@/components/clinic/manage/ServicePreInstructions.vue'
// import ServicePostInstructions from '@/components/clinic/manage/ServicePostInstructions.vue'
// import ServiceProvider from '@/components/clinic/manage/ServiceProvider.vue'

export default {
  name: 'Package',
  components: {},
  props: ['id', 'savePage', 'saveTab', 'saveSearch', 'saveFilters'],
  data () {
    return {
      isLoading: false,
      isFetching: false,
      isSearching: false,
      isSelecting: false,
      pageSize: -1,
      searchProducts: null,
      product: null,
      packageName: null,
      packagePrice: null,
      packageData: {},
      hqPrice: null,
      syncHqPrice: true,
      isAddingOverride: false,
      products: [],
      treatments: [],
      hqReadonlyHeaders: [
        { text: 'Name', value: 'name', class: 'text-table-header' },
        { text: 'Type', value: 'type', class: 'text-table-header' },
        { text: 'Price', value: 'price', class: 'text-table-header' },
        { text: 'Available', value: 'stock', class: 'text-table-header' },
        { text: 'Quantity', value: 'quantity', class: 'text-table-header' },
      ],
      headers: [
        { text: 'Name', value: 'name', class: 'text-table-header' },
        { text: 'Type', value: 'type', class: 'text-table-header' },
        { text: 'Price', value: 'price', class: 'text-table-header' },
        { text: 'Stock', value: 'stock', class: 'text-table-header' },
        { text: 'Quantity', value: 'quantity', class: 'text-table-header' },
        { text: 'Action', value: 'action', class: 'text-table-header' },
      ],
      rules: {
        not_empty: [v => (v != null && v.toString().trim().length) || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 0 || `Amount should be more than ${this.$currency.symbol} 0`],
        not_empty_select: [v => !!v || 'This field is required']
      },
    }
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
      form: 'clinic/getForm',
      isDoctor: 'user/isDoctor',
    }),
    ...mapGetters('hq', ['canOverridePrice']),
    // packageForm: {
    //   get () {
    //     let data = this.tempProduct
    //     if (!data.product) {
    //       data.product = {}
    //     }
    //     return data
    //   },
    //   set (val) {
    //     this.$store.commit('clinic/updateTempService', val)
    //   }
    // },
    packageValue () {
      let value = null
      if (this.treatments.length) {
        value = this.treatments.map(m => (Number(m.quantity || 0) * m.price)).reduce((total, num) => { return total + num }, 0)
        value = value / this.$currency.multiplier
      }
      return value
    },
    packageDiscount () {
      let discount = null
      if (this.packageValue && this.packagePrice && this.packageValue > this.packagePrice) {
        let perc = ((this.packageValue - Number(this.packagePrice)) / this.packageValue) * 100
        discount = this.limitToTwoDecimalPlaces(perc)
      }
      return discount
    },
    isHqPackage () {
      return !!this.packageData?.product?.branch_for
    },
    isBranchHqPackage () {
      return this.isHqPackage && !!this.company?.branch_of
    },
    isAdmin () {
      return this.member.roles.includes('Admin')
    },
    isAdminOrDoctor () {
      if (this.isAdmin || this.isDoctor) return true
      return false
    },
    canEditPriceOverride () {
      if (!this.isBranchHqPackage) return false
      return this.canOverridePrice
    },
    hqDiscount () {
      const hqPriceDisplay = this.hqPrice != null ? this.hqPrice / this.$currency.multiplier : null
      if (this.packageValue && hqPriceDisplay && this.packageValue > hqPriceDisplay) {
        let perc = ((this.packageValue - Number(hqPriceDisplay)) / this.packageValue) * 100
        return this.limitToTwoDecimalPlaces(perc)
      }
      return null
    },
    allowedPriceEdit () {
      // HQ packages: price is always read-only
      if (this.isHqPackage) return false
      // create package can edit price
      if (!this.id) return true
      // admin can edit price
      if (this.isAdmin) return true
      // 'lock_treatment_plan' is for CA
      if (this.company.genes.find(gene => gene.feature === 'lock_treatment_plan') && !this.isAdminOrDoctor) return false
      // 'lock_treatment_plan_price_edit' is for doctor
      if (this.company.genes.find(gene => gene.feature === 'lock_treatment_plan_price_edit') && this.isDoctor) return false
      return true
    },
  },
  watch: {
    searchProducts (val) {
      if (val) {
        this.fetchProducts(val)
      }
    }
  },
  mounted () {
    // this.fetchProducts()
    if (this.id) {
      this.fetchPackages()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchPackages () {
      this.isFetching = true
      this.$axios.get(`/clinics/${this.company.slug}/packages/${this.id}`)
        .then(resp => {
          this.packageData = resp.data
          this.treatments = resp.data.treatments.map(m => { return { ...m, quantity: m.quantity_per_set, selected_uom: (m.per_set && m.per_set.uom) || m.uom || null }})
          this.packageName = resp.data.product.name
          this.hqPrice = resp.data.product.hq_price != null ? resp.data.product.hq_price : null
          this.syncHqPrice = resp.data.product.sync_hq_price ?? true
          if (this.syncHqPrice) {
            this.packagePrice = null
          } else {
            this.packagePrice = resp.data.product.price != null ? resp.data.product.price / this.$currency.multiplier : null
          }
          // Fetch HQ inventory settings for override permission
          if (resp.data.product?.branch_for && this.company?.branch_of) {
            this.$store.dispatch('hq/fetchInventorySettings', { axios: this.$axios, hqId: this.company.branch_of.id })
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    fetchProducts (search) {
      this.isSearching = true
      let params = {
        relations: 'medication,service',
        status: 'published',
        ordering: '-inventory_qty,medication,name'
      }
      if (search) {
        params.search = search
      }
      this.$axios.get(`/clinics/${this.company.slug}/treatment_products`, { params })
        .then(resp => {
          this.products = resp.data.results
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isSearching = false
        })
    },
    addToList (product) {
      if (product) {
        if (this.treatments.find(f => f.id === product.id)) {
          return
        }
        let params = { 
          company: this.company.id,
          product: product.id,
          status: 'active',
          page_size: this.pageSize
        }
        this.isSelecting = true
        this.$axios.get(`/product_pricetiers`, { params })
          .then(resp => {
            let price = 0
            if (resp.data && resp.data.results) {
              let prices = resp.data.results.map(m => m.price || 0)
              price = Math.max(...prices)
            }
            this.treatments.push({ ...product, price, selected_uom: product.uom || null })
            this.$refs.autocmplt.blur()
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.isSelecting = false
          })
      }
    },
    saveData () {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
        return
      }
      // HQ packages: save price override
      if (this.isHqPackage) {
        if (this.syncHqPrice && !this.isAddingOverride) {
          this.goToPackages()
          return
        }
        this.isLoading = true
        const data = {
          product: {
            price: Math.round(Number(this.packagePrice) * Number(this.$currency.multiplier)),
            sync_hq_price: false
          }
        }
        this.$axios.patch(`/clinics/${this.company.slug}/packages/${this.id}`, data)
          .then(() => {
            this.syncHqPrice = false
            this.isAddingOverride = false
            this.showMessage(['success', this.$t('success_message.update_successful')])
            this.goToPackages()
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.isLoading = false
          })
        return
      }
      if (!this.treatments.length) {
        this.showMessage(['error', 'Please attach inventory and services' ])
        return
      }
      this.isLoading = true
      let data = {
        product: {
          name: this.packageName,
          price: Math.round(Number(this.packagePrice) * Number(this.$currency.multiplier))
        },
        treatments: this.treatments.map(m => { return { product: m.id, quantity_per_set: m.quantity, ...(m.selected_uom ? { uom: m.selected_uom } : {}) } })
      }
      if (this.id) {
        const endpoint = `/clinics/${this.company.slug}/packages/${this.id}`
        this.$axios.patch(endpoint, data)
          .then(resp => {
            let activity = 'Update package'
            this.updateActivityLog(endpoint, data, resp, 'PATCH', activity, this.id )
            this.goToPackages()
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.isLoading = false
          })    
      } else {
        const endpoint = `/clinics/${this.company.slug}/packages`
        this.$axios.post(endpoint, data)
          .then(resp => {
            let activity = 'Create new package'
            this.updateActivityLog(endpoint, data, resp, 'POST', activity)
            this.isLoading = false
            this.goToPackages()
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
            this.isLoading = false
          })
      }
    },
    onAddOverride () {
      this.isAddingOverride = true
      this.packagePrice = null
    },
    deleteTreatment (product) {
      this.treatments = this.treatments.filter(f => f.id !== product.id)
    },
    clearAutocomplete () {
      this.product = null
      this.searchProducts = null
      setTimeout(() => {
        this.products = []
      }, 300)
    },
    goToPackages () {
      let params = {
        savePage: this.savePage,
        saveTab: this.saveTab,
        saveSearch: this.saveSearch,
        saveFilters: this.saveFilters
      }
      this.$router.push({ name: 'ClinicPackages', params })
    },
    hasStock(item) {
      if (!item.inventory_enabled) {
        return true
      }
      if (item.inventory_qty > 0) {
        return true
      }
      return false
    },
    getTypeIcon (item) {
      if (item.medication) {
        return '$yzi-colors'
      }
      if (item.type === 'documents') {
        return '$yzi-file-06'
      }
      return 'mdi-briefcase-outline'
    },
    getStockUnit (treatment) {
      if (treatment.uom_conversion_enabled && treatment.uom_conversions && treatment.uom_conversions.length) {
        return treatment.uom_conversions[treatment.uom_conversions.length - 1].convert_to || ''
      }
      return treatment.uom || ''
    },
    getUomRules (treatment) {
      const validUnits = this.getUomOptions(treatment)
      return [
        v => !!v || 'Unit is required',
        v => validUnits.includes(v) || 'Select valid unit'
      ]
    },
    getUomOptions (treatment) {
      const units = []
      if (treatment.uom_conversions) {
        treatment.uom_conversions.forEach(c => {
          if (c.uom && !units.includes(c.uom)) units.push(c.uom)
          if (c.convert_to && !units.includes(c.convert_to)) units.push(c.convert_to)
        })
      }
      return units
    },
    limitToTwoDecimalPlaces(num) {
      // Check if the number is a float
      if (Number.isFinite(num) && !Number.isInteger(num)) {
          // If it's a float, round it to two decimal places
          return parseFloat(num.toFixed(2));
      }
      // If it's not a float, return the number as is
      return num;
    },
    updateActivityLog (endpoint, payload, response, method, activity, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'service',
        activity: activity,
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    }
  },
  destroyed () {
    // this.$store.commit('clinic/resetService')
    // this.$store.commit('clinic/resetTempService')
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  } 
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
  .autocmplt {
    .v-input__append-inner {
      margin-top: 12px !important;
    }
  }
}
.empty-box {
  position: relative;
  border-radius: 10px;
  border: var(--v-basic500) dashed 2px;
}
.button-box {
  border-left: solid 1px var(--v-neutral400);
  border-top: solid 1px var(--v-neutral400);
  border-bottom: solid 1px var(--v-neutral400);
  border-radius: 10px 0 0 10px;
  background-color: var(--v-neutral200);
}
</style>